/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import React, { CSSProperties } from 'react'

import { ActivityCardContent } from '@/components/quality-explorer/carousel/ActivityCardContent'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityCarouselCardProps = {
  /** The activity to display on the card. */
  activity: Activity

  /** The offset of the card from the center card. */
  offset: number

  /** The mode of the carousel (showing 1 cards, or 2). */
  mode: 1 | 2

  /** A function to call when the card is clicked. */
  onClick?: () => void

  /** Additional classes to apply to the card. */
  className?: string
}

/**
 * A card that displays information about an activity. The card is styled to appear in a 3D carousel, with the center
 * card being the largest and most prominent, and the other cards being smaller and semi-transparent.
 */
const ActivityCarouselCard: React.FC<ActivityCarouselCardProps> = ({ activity, offset, mode, onClick, className }) => {
  /**
   * Generate the `style` attributes for the card based on the current offset and mode. This gives the card a 3D
   * effect when it is offset from the center card by manipulating the `transform` property. The key is the
   * `translateX` value, which moves the card horizontally and `scale` which shrinks the card as it moves away from
   * the center. The `opacity` is also adjusted to make the card semi-transparent when it is not the center card.
   * The numbers used in the calculations are very specific to the design and should not be changed without testing.
   */
  const getCardStyle = (): CSSProperties => {
    const isActive = offset === 0
    const isTwoCardMode = mode === 2

    // Base values for active card
    let translateX = isActive ? (isTwoCardMode ? 30 : 22.5) : 0
    let translateZ = 0
    let rotateY = 0
    let scaleValue = 1

    if (isTwoCardMode && offset === 1) {
      translateX = 132.5
    } else if (offset > (isTwoCardMode ? 1 : 0)) {
      translateX = isTwoCardMode ? 132.5 + 15 + (offset - 1) * 11.5 : 35 + offset * 7.5
      translateZ = -100 - (isTwoCardMode ? offset - 1 : offset) * 50
      rotateY = 30
      scaleValue = Math.max(isTwoCardMode ? 0.85 - (offset - 1) * 0.1 : 0.9 - offset * 0.1, 0.5)
    } else if (!isActive) {
      translateX = offset * (isTwoCardMode ? 11.5 : 7.5) + (isTwoCardMode ? 15 : 10)
      translateZ = -100 + offset * 50
      rotateY = -30
      scaleValue = Math.max((isTwoCardMode ? 0.85 : 0.9) + offset * 0.1, 0.5)
    }

    // Opacity is based on the card's distance from the center (and varies slightly in two-card mode as two are visible)
    const opacity = Math.max(
      offset > 1 || offset <= -1 ? 1 - Math.abs(isTwoCardMode && offset > 1 ? offset - 1 : offset) * 0.25 : 1,
      0,
    )

    return {
      transform:
        opacity === 0
          ? `translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scaleValue})`
          : `translateX(${translateX}%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scaleValue})`,
      zIndex: 100 - Math.abs(offset * 10),
      opacity,
      pointerEvents: opacity === 0 ? 'none' : 'auto',
    }
  }

  return (
    <div
      className={cn(
        'col-start-1 row-start-1 transition-all duration-500 motion-reduce:transition-none',
        offset !== 0 && 'cursor-pointer',
        className,
      )}
      style={getCardStyle()}
      onClick={onClick}
      data-offset={offset}
    >
      {/* In two-card mode both forefront cards (offsets 0 and 1) are full-size, so both offer read-more. */}
      <ActivityCardContent activity={activity} isActive={offset === 0 || (mode === 2 && offset === 1)} />
    </div>
  )
}

export { ActivityCarouselCard }
